import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import FileUploader from '../FileUploader';

interface TestimonialItem {
  id: string;
  quote: string;
  author: string;
  role: string;
  avatar: string;
  sort_order: number;
}

interface TestimonialsEditorProps {
  data: TestimonialItem[];
  onSave: (data: TestimonialItem[]) => void;
}

const TestimonialsEditor: React.FC<TestimonialsEditorProps> = ({ data, onSave }) => {
  const [items, setItems] = useState<TestimonialItem[]>(data || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [avatar, setAvatar] = useState<string[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const doSave = (newItems: TestimonialItem[]) => {
    setItems(newItems);
    onSave(newItems);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    doSave(newItems.map((item, i) => ({ ...item, sort_order: i + 1 })));
  };

  const columns = [
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      sorter: (a: TestimonialItem, b: TestimonialItem) => a.sort_order - b.sort_order,
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 120,
    },
    {
      title: '职位',
      dataIndex: 'role',
      key: 'role',
      width: 150,
    },
    {
      title: '评价内容',
      dataIndex: 'quote',
      key: 'quote',
      render: (text: string) => (
        <div dangerouslySetInnerHTML={{ __html: text.substring(0, 100) + (text.length > 100 ? '...' : '') }} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_: any, record: TestimonialItem, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0}
            onClick={() => handleMove(index, 'up')} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={index === items.length - 1}
            onClick={() => handleMove(index, 'down')} />
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(index)}>编辑</Button>
          <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(index)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    setAvatar([]);
    setModalVisible(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    const item = items[index];
    form.setFieldsValue({
      id: item.id,
      quote: item.quote,
      author: item.author,
      role: item.role,
      sort_order: item.sort_order,
    });
    setAvatar(item.avatar ? [item.avatar] : []);
    setModalVisible(true);
  };

  const handleDelete = (index: number) => {
    doSave(items.filter((_, i) => i !== index));
    message.success('删除成功');
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const newItem: TestimonialItem = {
        id: values.id || `testimonial-${Date.now()}`,
        quote: values.quote,
        author: values.author,
        role: values.role,
        avatar: avatar[0] || '',
        sort_order: values.sort_order,
      };

      let newItems: TestimonialItem[];
      if (editingIndex !== null) {
        newItems = [...items];
        newItems[editingIndex] = newItem;
      } else {
        newItems = [...items, newItem];
      }
      doSave(newItems);
      setModalVisible(false);
      message.success(editingIndex !== null ? '更新成功' : '添加成功');
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加用户评价
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        pagination={false}
      />

      <Modal
        title={editingIndex !== null ? '编辑用户评价' : '添加用户评价'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={900}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="ID"
            name="id"
            rules={[{ required: editingIndex !== null, message: '请输入 ID' }]}
          >
            <Input placeholder="自动生成" disabled={editingIndex === null} />
          </Form.Item>

          <Form.Item
            label="评价内容"
            name="quote"
            rules={[{ required: true, message: '请输入评价内容' }]}
          >
            <Input.TextArea
              placeholder="支持 Markdown 格式&#10;例如：**粗体** *斜体* [链接](url)"
              rows={4}
            />
          </Form.Item>

          <Form.Item
            label="作者"
            name="author"
            rules={[{ required: true, message: '请输入作者' }]}
          >
            <Input placeholder="例如：张医生" />
          </Form.Item>

          <Form.Item
            label="职位"
            name="role"
            rules={[{ required: true, message: '请输入职位' }]}
          >
            <Input placeholder="例如：儿科主任医师" />
          </Form.Item>

          <Form.Item label="头像">
            <FileUploader
              category="image"
              value={avatar}
              onChange={setAvatar}
              maxCount={1}
              useAdminAPI={true}
            />
          </Form.Item>

          <Form.Item
            label="排序"
            name="sort_order"
            rules={[{ required: true, message: '请输入排序' }]}
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TestimonialsEditor;
